import React, { useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useChatSessions } from '../../context/ChatSessionContext';
import { useSession } from '../../context/SessionContext';
import { useUI } from '../../context/UIContext';
import { useConfig } from '../../context/ConfigContext';
import DeleteChatSessionDialog from './DeleteChatSessionDialog';
import { stripTagsForPreview } from '../../utils/textProcessing';
import ConditionalTooltip from '../common/ConditionalTooltip';

const ChatSessionNavigator = () => {
  const {
    chatSessions: allChatSessions,
    isLoadingChatSessions,
    chatSessionsError,
    activeChatSessionId: contextActiveSessionId,
    setActiveChatSessionId,
    deleteChatSession,
  } = useChatSessions();
  const theme = useTheme();
  const {
    sessionId: currentRuntimeSessionId,
    initializeSession,
    isInitializing: isRuntimeSessionInitializing,
    isConnected: isRuntimeSessionConnected,
  } = useSession();
  const { isMobile, setLeftSidebarOpen, isNavigationLocked } = useUI();
  const { startSessionButtons } = useConfig();
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState('');

  const teamIconMap = useMemo(() => {
    const map = new Map();
    if (startSessionButtons) {
      for (const buttonConfig of startSessionButtons) {
        map.set(buttonConfig.teamName, buttonConfig.icon);
      }
    }
    return map;
  }, [startSessionButtons]);

  const getTeamIcon = useCallback((teamName) => {
    if (teamIconMap.has(teamName)) return teamIconMap.get(teamName);
    if (teamIconMap.has(null)) return teamIconMap.get(null);
    return <ForumIcon />;
  }, [teamIconMap]);

  const { savedSessions, historySessions } = useMemo(() => {
    if (!allChatSessions) return { savedSessions: [], historySessions: [] };
    const saved = [];
    const history = [];
    const sortedSessions = [...allChatSessions].sort((a, b) => new Date(b.last_activity_at) - new Date(a.last_activity_at));
    
    sortedSessions.forEach(session => {
      // REMOVED: The filter that hid the current session
      // if (session.id === currentRuntimeSessionId && !session.save_enabled) {
      //   return;
      // }
      
      if (session.save_enabled) {
        saved.push(session);
      } else {
        history.push(session);
      }
    });
    return { savedSessions: saved, historySessions: history };
  }, [allChatSessions, currentRuntimeSessionId]);

  const handleOpenDeleteDialog = useCallback((session, event) => {
    event.stopPropagation();
    setSessionToDelete(session);
    setDeleteDialogError('');
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    if (isDeleting) return;
    setSessionToDelete(null);
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    setDeleteDialogError('');
    try {
      await deleteChatSession(sessionToDelete.id);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Error deleting session from navigator:", error);
      setDeleteDialogError(error.message || "Failed to delete session.");
    } finally {
      setIsDeleting(false);
    }
  }, [sessionToDelete, deleteChatSession, handleCloseDeleteDialog]);

  const handleSessionClick = useCallback(async (session) => {
    const sessionClickedId = session.id;
    console.log(`[ChatSessionNavigator] Session clicked: ${sessionClickedId}`);
    setActiveChatSessionId(sessionClickedId);
    
    if (isMobile) {
      setLeftSidebarOpen(false);
    }

    if (isRuntimeSessionInitializing) {
        console.warn("[ChatSessionNavigator] Runtime session initialization already in progress. Click ignored.");
        return;
    }

    if (currentRuntimeSessionId === sessionClickedId && isRuntimeSessionConnected) {
        console.log(`[ChatSessionNavigator] Runtime session ${sessionClickedId} is already active and connected. No action needed.`);
        return;
    }

    const options = {};
    if (session.team_name) {
      options.team_name = session.team_name;
    }
    
    await initializeSession(sessionClickedId, options);
    console.log(`[ChatSessionNavigator] Handed off initialization for ${sessionClickedId} to useSession hook.`);
  }, [
      setActiveChatSessionId,
      currentRuntimeSessionId,
      isRuntimeSessionConnected,
      isRuntimeSessionInitializing,
      initializeSession,
      isMobile,
      setLeftSidebarOpen,
  ]);

  const renderSessionList = (sessions) => (
    <List component="nav" dense sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, p: 0 }}>
      {sessions.map((session) => {
        // Highlight if it matches the context ID OR the runtime ID
        const isSelectedInList = contextActiveSessionId === session.id || currentRuntimeSessionId === session.id;
        
        const sessionName = session.name || `Chat from ${new Date(session.last_activity_at).toLocaleDateString()}`;
        const preview = stripTagsForPreview(session.preview_text) || 'No preview available';
        const lastActivity = new Date(session.last_activity_at).toLocaleString([], {
            dateStyle: 'short',
            timeStyle: 'short',
        });

        return (
          <ListItemButton
            key={session.id}
            onClick={() => handleSessionClick(session)}
            selected={isSelectedInList}
            disabled={isRuntimeSessionInitializing || (isNavigationLocked && !isSelectedInList)}
            sx={{
              py: 1,
              pl: 2,
              pr: 1.5,
              minHeight: '56px',
              opacity: (isNavigationLocked && !isSelectedInList) ? 0.5 : 1,
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) }
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              borderBottom: `1px solid ${theme.palette.divider}`,
              '&:last-child': {
                borderBottom: 'none',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: isSelectedInList ? theme.palette.primary.main : 'inherit', alignSelf: 'center' }}>
                {React.cloneElement(getTeamIcon(session.team_name), { fontSize: 'small' })}
              </ListItemIcon>
              <ConditionalTooltip title={sessionName} placement="top-start">
                <ListItemText
                  primary={sessionName}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isSelectedInList ? 'bold' : 500,
                    noWrap: true,
                    color: isSelectedInList ? theme.palette.primary.main : 'text.primary',
                  }}
                  sx={{ my: 0, flexGrow: 1, mr: 1 }}
                />
              </ConditionalTooltip>
              <ConditionalTooltip title="Delete Session">
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenDeleteDialog(session, e)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) },
                    p: 0.5,
                    alignSelf: 'center'
                  }}
                  aria-label={`delete session ${sessionName}`}
                  disabled={isDeleting && sessionToDelete?.id === session.id}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ConditionalTooltip>
            </Box>
            <ConditionalTooltip title={preview} placement="bottom-start">
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{
                  pl: '52px',
                  width: 'calc(100% - 52px)',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {preview}
              </Typography>
            </ConditionalTooltip>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                pl: '52px',
                width: 'calc(100% - 52px)',
                display: 'block',
                fontSize: '0.7rem',
                mt: 'auto',
              }}
            >
              {lastActivity}
            </Typography>
          </ListItemButton>
        );
      })}
    </List>
  );

  if (isLoadingChatSessions && (!allChatSessions || allChatSessions.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 1 }}>Loading chats...</Typography>
      </Box>
    );
  }

  if (chatSessionsError) {
    return <Alert severity="error" sx={{ m: 1, fontSize: '0.8rem' }}>{`Error: ${chatSessionsError}`}</Alert>;
  }

  if (savedSessions.length === 0 && historySessions.length === 0 && (!startSessionButtons || startSessionButtons.length === 0)) {
    return <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No other learning sessions.</Typography>;
  }

  return (
    <>
      {savedSessions.length > 0 && (
        <>
          <Divider sx={{ mt: 2, mb: 1, mx: 1 }}>
            <Chip icon={<BookmarkIcon fontSize="small" />} label="Saved Sessions" size="small" />
          </Divider>
          {renderSessionList(savedSessions)}
        </>
      )}
      {historySessions.length > 0 && (
        <>
          <Divider sx={{ mt: 2, mb: 1, mx: 1 }}>
            <Chip icon={<HistoryIcon fontSize="small" />} label="Recent History" size="small" />
          </Divider>
          {renderSessionList(historySessions)}
        </>
      )}
      {sessionToDelete && (
        <DeleteChatSessionDialog
          open={!!sessionToDelete}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          sessionName={sessionToDelete?.name || `Chat from ${new Date(sessionToDelete?.last_activity_at).toLocaleDateString()}`}
          isDeleting={isDeleting}
          error={deleteDialogError}
        />
      )}
    </>
  );
};

export default ChatSessionNavigator;